import { useState } from 'react';
import { CreditCard } from 'lucide-react';
import { formatCurrency } from '@/lib/format.js';
import { cn } from '@/lib/utils.js';

export type ManualCardDetails = {
  amount: number;
  transactionId: string;
  cardType: '' | 'visa' | 'mastercard' | 'amex' | 'discover' | 'debit' | 'other';
  lastFour: string;
};

const CARD_TYPES: Array<{ id: ManualCardDetails['cardType']; label: string }> = [
  { id: 'visa', label: 'Visa' },
  { id: 'mastercard', label: 'MC' },
  { id: 'amex', label: 'Amex' },
  { id: 'discover', label: 'Discover' },
  { id: 'debit', label: 'Debit' },
  { id: 'other', label: 'Other' },
];

export function ManualCardPaymentForm({
  invoiceTotal,
  maxAmount,
  value,
  onChange,
  helperText,
  readerAmount,
}: {
  invoiceTotal: number;
  maxAmount: number;
  value: ManualCardDetails;
  onChange: (next: ManualCardDetails) => void;
  helperText?: string;
  /** Cash price to key into a reader that adds its own surcharge. */
  readerAmount?: number;
}) {
  const [touched, setTouched] = useState(false);
  const amountOk = value.amount > 0 && value.amount <= maxAmount + 0.001;
  const lastFourOk = !value.lastFour || /^\d{0,4}$/.test(value.lastFour);

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
        <CreditCard className="h-4 w-4" />
        Card Payment
      </div>
      {readerAmount != null ? (
        <div className="mb-3 rounded-xl bg-white px-3 py-3">
          <div className="flex justify-between text-base font-bold text-slate-950">
            <span>Amount due</span>
            <span>{formatCurrency(readerAmount)}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Enter this amount into the card reader. The reader adds the surcharge.
          </p>
        </div>
      ) : (
        <div className="mb-3 flex justify-between text-sm text-slate-600">
          <span>Invoice total</span>
          <span className="font-semibold text-slate-950">{formatCurrency(invoiceTotal)}</span>
        </div>
      )}
      {helperText ? <p className="mb-3 text-xs text-slate-500">{helperText}</p> : null}

      <label className="block text-sm font-medium text-slate-700">
        Amount charged *
        <input
          type="number"
          min={0.01}
          max={maxAmount}
          step="0.01"
          className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
          value={value.amount || ''}
          onChange={(e) => onChange({ ...value, amount: Number(e.target.value || 0) })}
          onBlur={() => setTouched(true)}
        />
      </label>
      {touched && !amountOk ? (
        <p className="mt-1 text-xs font-semibold text-red-600">
          Amount must be greater than 0 and cannot exceed {formatCurrency(maxAmount)}
        </p>
      ) : (
        <p className="mt-1 text-xs text-slate-500">
          {readerAmount != null
            ? 'What the customer is charged after the reader adds the fee. Do not type this into the reader.'
            : 'Cannot exceed remaining balance'}
        </p>
      )}

      <label className="mt-3 block text-sm font-medium text-slate-700">
        Transaction ID (optional)
        <input
          type="text"
          maxLength={255}
          className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
          placeholder="e.g. Auth code, terminal ref number"
          value={value.transactionId}
          onChange={(e) => onChange({ ...value, transactionId: e.target.value })}
        />
      </label>

      <div className="mt-3">
        <p className="text-sm font-medium text-slate-700">Card type (optional)</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {CARD_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-semibold',
                value.cardType === t.id
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-700 ring-1 ring-slate-200',
              )}
              onClick={() =>
                onChange({
                  ...value,
                  cardType: value.cardType === t.id ? '' : t.id,
                })
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <label className="mt-3 block text-sm font-medium text-slate-700">
        Last 4 digits (optional)
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          className="mt-1 w-28 rounded-xl border border-slate-300 bg-white px-3 py-2"
          placeholder="••••"
          value={value.lastFour}
          onChange={(e) =>
            onChange({ ...value, lastFour: e.target.value.replace(/\D/g, '').slice(0, 4) })
          }
        />
      </label>
      {!lastFourOk ? (
        <p className="mt-1 text-xs font-semibold text-red-600">Enter up to 4 digits</p>
      ) : null}
    </div>
  );
}

export function isManualCardValid(value: ManualCardDetails, maxAmount: number): boolean {
  return value.amount > 0 && value.amount <= maxAmount + 0.001;
}
